/**
 * Why `/fly`'s ground had "no rivers, no fields, nothing" (user report,
 * 2026-08-19), and the geometry this module builds to fix it.
 *
 * **The finding.** A large part of Red Baron's terrain is not polygons at all
 * in any modern sense — it is *polylines stored as zero-width quads*. A
 * Verdun road or river is a strip of quads whose two long edges are **one raw
 * model unit apart**: 1/65 m, about 1.5 cm. Ten of Verdun's fifty distinct
 * scenery objects (`TERR5-00/02/04/06/08/12/14/16/37/75`) are nothing but
 * these; across the region 141 of their faces carry BARON.PAL index 4
 * (`#304965`, the river blue) and ~200 more carry the road greys 67/68/70.
 *
 * The original's software rasterizer paints at least one pixel per scanline
 * crossing, so it draws every one of those as a crisp **1-pixel line** —
 * measured, not inferred: a straight-down capture at 4,710 ft over Verdun
 * (`re-sessions/terrain/shots/alt5000-down.png`) has the river exactly 2 px
 * wide in a 640-px-wide grab of a 320-px-wide mode, i.e. one native pixel, at
 * every row sampled. A GPU rasterizer instead shades a pixel only when its
 * sample centre is covered, so a 1.5 cm-wide quad covers nothing and **the
 * entire road and river network renders to zero pixels**. That is the whole of
 * "no rivers".
 *
 * **The fix, stated generally.** The same rasterizer rule also keeps a *large*
 * polygon visible once distance shrinks it below a pixel — the original's
 * ground never dissolves the way ours did. So rather than special-casing the
 * degenerate quads (there is no clean width threshold to split on: the corpus
 * has triangles at every width from 0 upward), this module reproduces the rule
 * itself — **every triangle's three edges are also drawn as a
 * one-original-pixel line in that face's own colour.**
 *
 * Because the line takes the face's colour, a polygon that is already several
 * pixels wide is unchanged (its outline is invisible against its own fill, and
 * so are the interior diagonals of a fan-triangulated face); a polygon that has
 * shrunk below a pixel still shows, exactly as it does in the original; and a
 * zero-width ribbon *is* its own outline. No colour is invented anywhere.
 *
 * The arithmetic is kept three.js-free so it is unit-testable without a GPU;
 * `hairline-mesh.ts` turns the buffers into the drawable.
 */

/** The original's framebuffer width. Its rasterizer's floor is one pixel of *this*, so that is the width a line has to keep to look the same at any output resolution. */
export const ORIGINAL_FRAMEBUFFER_WIDTH_PX = 320;

/** Vertices per triangle in a non-indexed buffer. */
const CORNERS = 3;
/** Floats per segment: two endpoints of three components each. */
const FLOATS_PER_SEGMENT = 6;

/**
 * One original pixel, expressed in the **CSS pixels** of a view
 * `viewWidthCssPx` wide — the line width that makes a road subtend the same
 * fraction of the field of view it does in the original. Never thinner than one
 * CSS pixel, or it would disappear again for exactly the reason the source
 * quads did.
 *
 * **CSS pixels, not drawing-buffer pixels**, and the distinction is not
 * cosmetic — this took the drawing buffer's width until 2026-08-25, which was
 * right on a `devicePixelRatio` of 1 and wrong on every other display. `linewidth`
 * on a `Line2NodeMaterial` is consumed as `offset / (viewport.w / screenDPR)`
 * (three's `Line2NodeMaterial.js`), and three's `viewport` node is the viewport
 * *in device pixels* while `screenDPR` is the pixel ratio — so the denominator
 * is the view's CSS height and the numerator has to be a CSS width to match.
 * Feeding it the buffer width instead scaled every road and field edge by the
 * pixel ratio: **8.3 CSS px of road at ratio 2 against 13.7 at ratio 3**, in the
 * same window at the same pose, measured off the swapchain. A road's whole
 * contract is that it is 1/320 of the *view* at any output resolution, so the
 * view is what it has to be measured against.
 */
export function originalPixelWidth(viewWidthCssPx: number): number {
  return Math.max(1, viewWidthCssPx / ORIGINAL_FRAMEBUFFER_WIDTH_PX);
}

/** A line-segment list in a model's own (already world-oriented) frame: 6 position floats and 6 colour floats per segment. */
export interface SegmentSource {
  readonly positions: Float32Array;
  readonly colors: Float32Array;
  readonly segmentCount: number;
}

/** One baked LOD's 2-point faces, as `packages/baker`'s `ModelLodLines` spells them: model-frame positions, endpoint index pairs, one colour per segment. */
export interface LineFaces {
  readonly positions: readonly number[];
  readonly indices: readonly number[];
  readonly colors: readonly { readonly colorRgba: readonly number[] }[];
}

/** A shared non-indexed triangle mesh to outline: 3 vertices per triangle, 9 floats each of position and colour. */
export interface TriangleMesh {
  readonly positions: ArrayLike<number>;
  /** Per-vertex r,g,b in [0,1] — three.js `vertexColors` convention, which is how the baked flat face colour reaches the GPU. */
  readonly colors: ArrayLike<number>;
}

/** Where one copy of a `SegmentSource` sits: a yaw about world Y, then a translation — the same transform `world/placement-transform.ts` gives the instanced meshes. */
export interface SegmentPlacement {
  readonly yawRadians: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface SegmentBatch {
  readonly source: SegmentSource;
  readonly placements: readonly SegmentPlacement[];
}

/** Flat buffers in the layout an instanced line geometry wants: 6 floats per segment. */
export interface SegmentBuffers {
  readonly positions: Float32Array;
  readonly colors: Float32Array;
  readonly segmentCount: number;
}

const EMPTY: SegmentBuffers = { positions: new Float32Array(0), colors: new Float32Array(0), segmentCount: 0 };

/**
 * Expands a non-indexed triangle mesh into its edges — 3 segments per
 * triangle, each endpoint keeping its own vertex colour.
 *
 * Shared edges come out once per adjoining triangle here, because at this
 * level a face only knows its own outline. `buildSegmentBuffers` collapses
 * them once the whole region is in one frame — that is where two coincident
 * lines stop being an artefact of the expansion and become a depth tie, and
 * where the original's paint order is available to settle it.
 */
export function triangleOutlineSegments(mesh: TriangleMesh): SegmentSource {
  const triangles = Math.floor(mesh.positions.length / (CORNERS * 3));
  const segmentCount = triangles * CORNERS;
  const positions = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  const colors = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  let cursor = 0;

  for (let triangle = 0; triangle < triangles; triangle++) {
    const base = triangle * CORNERS * 3;
    for (let edge = 0; edge < CORNERS; edge++) {
      for (const corner of [base + edge * 3, base + ((edge + 1) % CORNERS) * 3]) {
        positions[cursor] = mesh.positions[corner]!;
        positions[cursor + 1] = mesh.positions[corner + 1]!;
        positions[cursor + 2] = mesh.positions[corner + 2]!;
        colors[cursor] = mesh.colors[corner]!;
        colors[cursor + 1] = mesh.colors[corner + 1]!;
        colors[cursor + 2] = mesh.colors[corner + 2]!;
        cursor += 3;
      }
    }
  }

  return { positions, colors, segmentCount };
}

/**
 * The 2-point faces the bake keeps beside the triangles (roads, rivers, field
 * boundaries, rigging), expanded into the same segment layout
 * `triangleOutlineSegments` produces — including the **same** model→world
 * frame fix, `(x, y, z) -> (x, z, -y)`, which the triangle path gets from
 * `geometry.rotateX(-PI/2)` instead. Both must agree vertex for vertex or a
 * model's lines would sit in a different frame from its own triangles; the
 * shared-fixture test in `test/polygon-outlines.test.ts` pins that.
 *
 * Written defensively against `lines` being absent because the field arrives
 * with a rebake, and a stale `data/baked` should degrade to "triangles only"
 * rather than throw.
 */
export function lineFaceSegments(lines: LineFaces | undefined): SegmentSource | undefined {
  if (!lines || lines.indices.length < 2) return undefined;
  const segmentCount = Math.floor(lines.indices.length / 2);
  const positions = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  const colors = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);

  for (let segment = 0; segment < segmentCount; segment++) {
    const rgba = lines.colors[segment]?.colorRgba ?? [255, 255, 255];
    for (const end of [0, 1]) {
      const vertex = (lines.indices[segment * 2 + end] ?? 0) * 3;
      const at = segment * FLOATS_PER_SEGMENT + end * 3;
      positions[at] = lines.positions[vertex] ?? 0;
      positions[at + 1] = lines.positions[vertex + 2] ?? 0;
      positions[at + 2] = -(lines.positions[vertex + 1] ?? 0);
      colors[at] = (rgba[0] ?? 0) / 255;
      colors[at + 1] = (rgba[1] ?? 0) / 255;
      colors[at + 2] = (rgba[2] ?? 0) / 255;
    }
  }
  return { positions, colors, segmentCount };
}

/** Concatenates segment sources; used to fold a model's explicit 2-point line faces in with its triangle outlines. */
export function concatSegments(sources: readonly SegmentSource[]): SegmentSource {
  const nonEmpty = sources.filter((source) => source.segmentCount > 0);
  if (nonEmpty.length === 0) return EMPTY;
  if (nonEmpty.length === 1) return nonEmpty[0]!;

  const segmentCount = nonEmpty.reduce((total, source) => total + source.segmentCount, 0);
  const positions = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  const colors = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  let offset = 0;
  for (const source of nonEmpty) {
    positions.set(source.positions, offset);
    colors.set(source.colors, offset);
    offset += source.segmentCount * FLOATS_PER_SEGMENT;
  }
  return { positions, colors, segmentCount };
}

/**
 * Endpoint quantisation for the coincidence test, in metres. Two segments
 * count as the same edge when both endpoints agree to a millimetre. Well below
 * anything the terrain distinguishes and well above float error in the
 * placement transform, with room to spare against the closest thing the data
 * has to a near-coincident pair: the two long edges of a zero-width river
 * ribbon, 1/65 m — 15 mm — apart (module doc).
 */
const COINCIDENT_TOLERANCE_M = 0.001;

/** An edge's identity, independent of which way round its two endpoints were listed. */
function edgeKey(positions: Float32Array, segment: number): string {
  const at = segment * FLOATS_PER_SEGMENT;
  const q = (value: number): number => Math.round(value / COINCIDENT_TOLERANCE_M);
  const a = `${q(positions[at]!)},${q(positions[at + 1]!)},${q(positions[at + 2]!)}`;
  const b = `${q(positions[at + 3]!)},${q(positions[at + 4]!)},${q(positions[at + 5]!)}`;
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Places every batch's segments into one world-space buffer pair, applying
 * each placement's yaw and translation, then **drops every edge that a later
 * segment draws again**.
 *
 * Why the de-duplication is not an optimisation. Every triangle contributes
 * all three of its edges, so an edge shared by two faces is emitted twice, at
 * *identical* world positions — and for Verdun that is 8,658 of 24,592
 * segments, 4,040 of them carrying two **different** colours because the two
 * faces are different colours. Two exactly coplanar, exactly coincident lines
 * is the one thing a depth buffer cannot arbitrate: which colour a field
 * boundary shows was decided by nothing, and changed under sub-metre camera
 * movement. That is the landscape z-fighting (user report, 2026-08-23);
 * measured, reversing the segment order repainted 1,212-2,478 px of every
 * frame, at every altitude.
 *
 * This module's header used to argue the winner was "as arbitrary as the
 * original's own fill rule". It is not: the original has no depth buffer and
 * paints in file order, so the shared edge simply belongs to whichever face is
 * drawn **later** (docs/formats/models-tbl.md §12.2 — the polygon handler at
 * `0x3DA8D` is a flat sequential loop over the face array, with no sort and no
 * per-face priority). Keeping the last of each duplicate group reproduces that
 * rule exactly, and it resolves the tie by removing it rather than by biasing
 * it — no material flag, no offset, nothing for the depth buffer to lose.
 *
 * `batches` must therefore arrive in the region's own paint order; see
 * `three/world-instancer.ts`, which assembles them in `.WLD` record order.
 */
export function buildSegmentBuffers(batches: readonly SegmentBatch[]): SegmentBuffers {
  let placedCount = 0;
  for (const batch of batches) placedCount += batch.source.segmentCount * batch.placements.length;
  if (placedCount === 0) return EMPTY;

  const placed = new Float32Array(placedCount * FLOATS_PER_SEGMENT);
  const placedColors = new Float32Array(placedCount * FLOATS_PER_SEGMENT);
  let cursor = 0;

  for (const batch of batches) {
    const source = batch.source;
    if (source.segmentCount === 0) continue;
    const endpoints = source.segmentCount * 2;

    for (const placement of batch.placements) {
      const cos = Math.cos(placement.yawRadians);
      const sin = Math.sin(placement.yawRadians);

      for (let endpoint = 0; endpoint < endpoints; endpoint++) {
        const at = endpoint * 3;
        const x = source.positions[at]!;
        const y = source.positions[at + 1]!;
        const z = source.positions[at + 2]!;
        // Y-axis yaw then translate, matching `THREE.Matrix4.makeRotationY`.
        placed[cursor] = cos * x + sin * z + placement.x;
        placed[cursor + 1] = y + placement.y;
        placed[cursor + 2] = -sin * x + cos * z + placement.z;
        placedColors[cursor] = source.colors[at]!;
        placedColors[cursor + 1] = source.colors[at + 1]!;
        placedColors[cursor + 2] = source.colors[at + 2]!;
        cursor += 3;
      }
    }
  }

  // Last writer of each edge wins, and the survivors keep their paint order.
  const lastAt = new Map<string, number>();
  for (let segment = 0; segment < placedCount; segment++) lastAt.set(edgeKey(placed, segment), segment);

  const segmentCount = lastAt.size;
  const positions = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  const colors = new Float32Array(segmentCount * FLOATS_PER_SEGMENT);
  let out = 0;
  for (let segment = 0; segment < placedCount; segment++) {
    if (lastAt.get(edgeKey(placed, segment)) !== segment) continue;
    const from = segment * FLOATS_PER_SEGMENT;
    positions.set(placed.subarray(from, from + FLOATS_PER_SEGMENT), out);
    colors.set(placedColors.subarray(from, from + FLOATS_PER_SEGMENT), out);
    out += FLOATS_PER_SEGMENT;
  }

  return { positions, colors, segmentCount };
}
